import React, { useRef, useState, useEffect } from "react";
import PopupBase from "../base/PopupBase";
import { DataProviderInstance } from '../../api/DataProvider';
import CustomButton from "../../components/buttons/CustomButton";
import ConfirmPopup from "../ConfirmPopup/ConfirmPopup";
import styles from './CreateAnamnesePopupContent.module.css';
import DynamicForm from "../../components/dynamicForm/DynamicForm";
import { FormControl, InputLabel, MenuItem, Select, Box, Typography, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

function CreateAnamnesePopupContent(props) {
    const { CompanyConfig, SetPatientAnamnese, UpdatePatientAnamnese, DeletePatientAnamnese } = DataProviderInstance();
    const navigate = useNavigate();
    
    // appointmentId: atendimento de origem (0 = anamnese avulsa); fixedTemplateId: modelo
    // fixado no tipo de atendimento (pula a escolha de modelo)
    const { currentDate, patientId, OnSave, OnHide, anamnese, appointmentId = 0, fixedTemplateId = null } = props;
    const dynamicFormRef = useRef(null);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showMainPopup, setShowMainPopup] = useState(true);
    const [selectedTemplate, setSelectedTemplate] = useState(() => {
        // Check if AnamneseTemplates exists and has items
        if (!CompanyConfig.AnamneseTemplates || CompanyConfig.AnamneseTemplates.length == 0) {
            return null;
        }

        if (anamnese) {
            const template = CompanyConfig.AnamneseTemplates.find(template => {
                return template.Id == anamnese.template;
            });

            if (!template) {
                return CompanyConfig.AnamneseTemplates[0];
            }
            
            return template;
        } else {
            const fixed = fixedTemplateId != null
                ? CompanyConfig.AnamneseTemplates.find(template => String(template.Id) === String(fixedTemplateId))
                : null;
            return fixed || CompanyConfig.AnamneseTemplates[0];
        }
    });

    const usingFixedTemplate = !anamnese && fixedTemplateId != null && selectedTemplate != null && String(selectedTemplate.Id) === String(fixedTemplateId);

    // Perguntas do formulário: ao editar, a cópia guardada no preenchimento (o modelo
    // pode ter mudado ou sido apagado); ao criar, o modelo selecionado
    function getQuestions() {
        if (anamnese && anamnese.questions_snapshot) {
            try {
                const snapshot = JSON.parse(anamnese.questions_snapshot);
                if (Array.isArray(snapshot)) return snapshot;
            } catch (error) {
                // cópia corrompida: cai no modelo
            }
        }
        return selectedTemplate ? JSON.parse(selectedTemplate.Questions) : [];
    }

    useEffect(() => {
        // Função para tentar preencher o formulário
        const tryToFillForm = (attempt = 1, maxAttempts = 10) => {
            if (dynamicFormRef.current && hasFormSource()) {
                if (anamnese) {
                    try {
                        const answers = JSON.parse(anamnese.anamnese);
                        dynamicFormRef.current.SetValue(answers);
                        return; // Sucesso, sair da função
                    } catch (error) {
                        return;
                    }
                } else {
                    dynamicFormRef.current.Reset();
                    return;
                }
            } else {
                // Se ainda não chegou ao máximo de tentativas, tentar novamente
                if (attempt < maxAttempts) {
                    setTimeout(() => tryToFillForm(attempt + 1, maxAttempts), 100);
                }
            }
        };
        
        // Iniciar tentativas de preenchimento
        tryToFillForm();
    }, [anamnese, selectedTemplate]);

    // Há o que mostrar: algum modelo cadastrado ou, ao editar, a cópia das perguntas
    function hasFormSource() {
        const hasTemplates = CompanyConfig.AnamneseTemplates && CompanyConfig.AnamneseTemplates.length > 0;
        return hasTemplates || !!(anamnese && anamnese.questions_snapshot);
    }

    function OnCloseClickHandler() {
        Hide();
    }

    function OnCancelClickHandler() {
        Hide();
    }

    function OnConfirmClickHandler() {
        if (dynamicFormRef.current.Validate()) {
            SaveData();
            //Hide();
        }
    }

    function OnDeleteClickHandler() {
        setShowMainPopup(false);
        setShowConfirmDelete(true);
    }

    function OnCancelDelete() {
        setShowConfirmDelete(false);
        Hide();
    }

    function ExecuteDelete() {
        if (anamnese) {
            DeletePatientAnamnese(anamnese.id, (response) => {
                setShowConfirmDelete(false);
                OnSave();
                Hide();
            });
        }
    }

    function Hide() {
        OnHide();
    }

    function SaveData() {
        const answers = dynamicFormRef.current.GetValue();
        const json = answers;

        if (anamnese) {
            UpdatePatientAnamnese(anamnese.id, patientId, json, (response) => {
                OnSave();
                Hide();
            });
        } else {
            SetPatientAnamnese(currentDate, patientId, appointmentId || 0, json, selectedTemplate.Id, (response) => {
                OnSave();
                Hide();
            });
        }
    }

    function GetTitle() {
        if (anamnese) return "Editar anamnese";
        return usingFixedTemplate ? `Preencher ${selectedTemplate.Label}` : "Criar nova anamnese";
    }

    function handleTemplateChange(event) {
        const selectedValue = event.target.value;
        const selectedTemplate = CompanyConfig.AnamneseTemplates.find(
            (template) => template.Label == selectedValue
        );
        setSelectedTemplate(selectedTemplate);
    }

    function GetContent() {
        // Check if templates exist
        if (!hasFormSource()) {
            return (
                <Paper
                    elevation={0}
                    sx={{
                        padding: '24px',
                        margin: '20px 0',
                        backgroundColor: '#f8f9fa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                    }}
                >
                    <Box sx={{ textAlign: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ mb: 2, color: '#424242', fontWeight: 500 }}>
                            Nenhum template de anamnese encontrado
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 3, color: '#616161', lineHeight: 1.6 }}>
                            Para criar uma anamnese, você precisa configurar templates através do menu
                            <strong> "Empresa &gt; Modelos"</strong> e ir em <strong>"Anamneses"</strong>.
                        </Typography>
                        <CustomButton
                            style="primary"
                            variant="contained"
                            icon={<SettingsOutlinedIcon />}
                            label="Ir para configuração de templates"
                            onClick={() => {
                                OnHide(); // Close current popup
                                navigate('/company/templates'); // Navigate to templates page
                            }}
                        />
                    </Box>
                </Paper>
            );
        }

        return (
            <div>
                {!anamnese && !usingFixedTemplate && (
                    <div className={styles.templatesContainer}>
                        Modelos:
                        <FormControl>
                            <Select
                                labelId="template-select-label"
                                id="template-select"
                                defaultValue={selectedTemplate?.Label}
                                onChange={handleTemplateChange}
                            >
                                {CompanyConfig.AnamneseTemplates.map((template) => (
                                    <MenuItem key={template.Label} value={template.Label}>
                                        {template.Label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>
                )}
                <DynamicForm
                    key={selectedTemplate ? selectedTemplate.label : "default"}
                    ref={dynamicFormRef}
                    questions={getQuestions()}
                />
            </div>
        );
    }

    function GetFooter() {
        // If no templates exist, only show cancel button
        if (!hasFormSource()) {
            return (
                <div style={{display:"flex", gap:"10px", justifyContent: "flex-end"}}>
                    <CustomButton
                        variant="outlined"
                        style="secondary"
                        label={"Fechar"}
                        onClick={OnCancelClickHandler}
                    />
                </div>
            );
        }

        return (
            <div style={{display:"flex", gap:"10px", justifyContent: anamnese ? "space-between" : "flex-end"}}>
                {anamnese && (
                    <CustomButton
                        variant="contained"
                        style="error"
                        label="Apagar anamnese"
                        onClick={OnDeleteClickHandler}
                    />
                )}
                <div style={{display:"flex", gap:"10px"}}>
                    <CustomButton
                        variant="outlined"
                        style="secondary"
                        label={"Cancelar"}
                        onClick={OnCancelClickHandler}
                    />

                    <CustomButton
                        style="primary"
                        variant="contained"
                        label={anamnese ? "Salvar alterações" : "Registrar"}
                        onClick={OnConfirmClickHandler}
                    />
                </div>
            </div>
        );
    }

    function GetRender() {
        return (
            <>
                {showMainPopup && (
                    <PopupBase
                        Title={GetTitle()}
                        Content={GetContent()}
                        Footer={GetFooter()}
                        OnClose={OnCloseClickHandler}
                    />
                )}
                <ConfirmPopup
                    Visibility={showConfirmDelete}
                    Title="Confirmar exclusão"
                    Description="Tem certeza que deseja apagar esta anamnese? Esta ação não pode ser desfeita."
                    ConfirmLabel="Apagar"
                    OnConfirm={ExecuteDelete}
                    OnCancel={OnCancelDelete}
                    OnHide={OnCancelDelete}
                />
            </>
        );
    }

    return GetRender();
}

export default CreateAnamnesePopupContent;